// frontend/src/components/customers/CustomerPicker.js
//
// Reusable typeahead for attaching a Customer to an Order or Sale —
// dropped into Orders.js and sales/NewSale.js. Unlike
// CommonComponents.SearchableDropdown (which filters an already-loaded
// in-memory list, fine for something small and static like the
// ambassador list), this does a live debounced server search against
// GET /api/customers/search, since a business's customer list can grow
// well past what's reasonable to load upfront.
//
// Fully self-contained: quick-adding a brand-new customer (name, phone,
// and address) happens right here via POST /api/customers, so every
// screen that embeds this only needs to hand it onSelect/onClear —
// no separate "new customer" modal to wire up per screen.
//
// Only rendered where hasCRM is true (Tier 13, Growth+) — the parent
// screen is responsible for that check, same as hasRawMaterials gating
// the materials picker in Orders.js.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as api from '../../api';

function CustomerPicker({ value, valueName, onSelect, onClear, onStartCreate, creationContext = "this order", disabled, showAlert, canCreate = true }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const runSearch = useCallback((term) => {
    clearTimeout(debounceRef.current);
    if (!term || term.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchCustomers(term.trim());
        setResults(r.customers || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  }, []);

  const handleChange = (v) => {
    setQuery(v);
    if (value) onClear();
    setOpen(true);
    setAdding(false);
    runSearch(v);
  };

  const quickAdd = async () => {
    if (!query.trim()) return;
    if (!newPhone.trim() || !newAddress.trim()) {
      showAlert && showAlert("Enter the customer's phone number and address before saving", "error");
      return;
    }
    setSaving(true);
    try {
      const c = await api.createCustomer({ name: query.trim(), phone: newPhone.trim(), address: newAddress.trim() });
      if (!c?.customerId) throw new Error("Customer could not be saved. Please try again.");
      onSelect(c);
      setQuery(""); setNewPhone(""); setNewAddress(""); setAdding(false); setOpen(false);
      showAlert && showAlert(`${c.name} added to Customers`);
    } catch (e) {
      showAlert && showAlert(e.message, "error");
    }
    setSaving(false);
  };

  const startAddingCustomer = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const name = query.trim();
    if (!name) return;
    // For an Order this immediately removes the need to type the same
    // name again in Client while the contact details are being collected.
    onStartCreate && onStartCreate(name);
    setAdding(true);
  };

  return (
    <div className="form-group" ref={ref} style={{ position: "relative" }}>
      <label>Customer (optional — selecting or adding one fills the client name)</label>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          value={value ? valueName : query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search by name or phone…"
          disabled={disabled}
          style={{ flex: 1, padding: "10px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff" }}
        />
        {value && (
          <button type="button" onClick={() => { onClear(); setQuery(""); }} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }} title="Clear">✕</button>
        )}
      </div>
      {open && !value && (
        <div style={{
          position: "absolute", left: 0, right: 0, top: "calc(100% + 2px)",
          background: "#fff", border: "1.5px solid #e94560", borderRadius: 8,
          boxShadow: "0 6px 20px rgba(0,0,0,.12)", zIndex: 200,
          maxHeight: 280, overflowY: "auto",
        }}>
          {adding ? (
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>New customer details</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>Save "{query.trim()}" to CRM, then continue completing {creationContext}.</div>
              <input
                value={newPhone} onChange={e => setNewPhone(e.target.value)}
                placeholder="Phone number *" required autoFocus
                style={{ width: "100%", padding: "8px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}
              />
              <input
                value={newAddress} onChange={e => setNewAddress(e.target.value)}
                placeholder="Address *" required
                style={{ width: "100%", padding: "8px 10px", border: "1.5px solid #e5e7eb", borderRadius: 6, fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: "6px 12px" }} disabled={saving} onClick={quickAdd}>
                  {saving ? "Saving…" : `Save customer and use for ${creationContext}`}
                </button>
                <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {searching ? (
                <div style={{ padding: "12px 14px", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>Searching…</div>
              ) : query.trim().length < 2 ? (
                <div style={{ padding: "12px 14px", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>Type at least 2 characters…</div>
              ) : results.length === 0 ? (
                <div style={{ padding: "12px 14px", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>No matching customers</div>
              ) : results.map(c => (
                <button
                  key={c.customerId}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); onSelect(c); setQuery(""); setOpen(false); }}
                  style={{ display: "block", width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", textAlign: "left", fontSize: 13 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fff8f9"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{c.phone || c.email || "—"}</div>
                </button>
              ))}
              {query.trim().length > 0 && canCreate && (
                <button
                  type="button"
                  onClick={startAddingCustomer}
                  style={{ display: "block", width: "100%", padding: "10px 14px", background: "#fff8f9", border: "none", cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 600, color: "#e94560" }}
                >
                  + Add "{query.trim()}" as a new customer
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerPicker;
